import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import ProductViewCompact from '../../product-view-compact'

const Item = ({
    bonus,
    selectedBonuses,
    childProductRefs,
    meterType,
    globalePrices,
    promoId,
    index,
    maxBonusItems,
    selectedItems,
    bonuses,
    handleClose,
    maxOrderQuantity,
    showProductDescription,
    setSelectedBonuses,
    setShowAddToBagError
}) => {
    const [showAddedToBag, setShowAddedToBag] = useState(false)

    const isBonusSelected = selectedBonuses?.some(
        (selected) => selected.variant.productId === bonus?.productId
    )

    const handleAddToBag = (variant, quantity) => {
        let wasRemoved = false

        setSelectedBonuses((prev) => {
            let result
            const existingBonus = prev.find(
                (bonus) => bonus.variant.productId === variant.productId
            )

            if (existingBonus) {
                result = prev.filter((bonus) => bonus.variant.productId !== variant.productId)
                wasRemoved = true
            } else {
                result = [...prev, {variant, quantity}]
            }

            return result
        })

        setShowAddedToBag(!wasRemoved)

        return {wasRemoved, variant, productSelectionValues: [variant]}
    }

    const handleVariantSelected = (product, variant, quantity) => {
        setShowAddToBagError(false)

        if (bonuses?.length === 1) {
            setSelectedBonuses([{variant, quantity}])
        } else {
            const isBonusSelected = selectedBonuses.some(
                (selected) => selected.variant.productId === variant.productId
            )

            setShowAddedToBag(isBonusSelected)
        }
    }

    return (
        <ProductViewCompact
            key={bonus?.productId || bonus?.id || index}
            ref={function (ref) {
                childProductRefs.current[bonus.id] = {
                    ref,
                    validateOrderability: this.validateOrderability
                }
            }}
            product={bonus}
            isProductPartOfSet={true}
            isProductPartOfBundle={false}
            styleVariant="gwp"
            meterType={meterType}
            globalePrices={globalePrices}
            promoId={promoId}
            limitReached={selectedItems === maxBonusItems}
            childOfBundleQuantity={1}
            enableFavourite={false}
            index={index}
            isShopTheLook={true}
            isProductLoading={false}
            showQtySelector={bonuses?.length > 1}
            enableQtySelector={maxBonusItems - selectedItems > 0}
            enableAddToBag={!isBonusSelected && maxBonusItems - selectedItems > 0}
            showSwatches={bonus?.type?.master === true}
            showAddToBag={bonuses?.length > 1}
            onTileClick={handleClose}
            addToCart={handleAddToBag}
            maxOrderQuantity={maxOrderQuantity}
            showDescription={showProductDescription}
            onVariantSelected={handleVariantSelected}
            showAddedToBagLabel={showAddedToBag}
        />
    )
}

Item.propTypes = {
    bonus: PropTypes.object,
    selectedBonuses: PropTypes.array,
    childProductRefs: PropTypes.object,
    meterType: PropTypes.any,
    selectedItems: PropTypes.any,
    globalePrices: PropTypes.any,
    promoId: PropTypes.any,
    index: PropTypes.number,
    maxBonusItems: PropTypes.number,
    totalSelectedBonuses: PropTypes.number,
    bonuses: PropTypes.array,
    handleClose: PropTypes.func,
    handleAddToBag: PropTypes.func,
    maxOrderQuantity: PropTypes.number,
    showProductDescription: PropTypes.bool,
    handleVariantSelected: PropTypes.func,
    showAddedToBag: PropTypes.bool,
    bonusItemsInBasketArray: PropTypes.any,
    setSelectedBonuses: PropTypes.func,
    setShowAddToBagError: PropTypes.func
}

const Body = ({
    bonuses,
    maxBonusItems,
    globalePrices = null,
    promoId = null,
    totalSelectedBonuses,
    bonusItemsInBasketArray,
    selectedBonuses,
    meterType,
    setSelectedBonuses,
    setShowAddToBagError,
    showProductDescription,
    handleClose
}) => {
    const selectedItems =
        (bonusItemsInBasketArray?.reduce((sum, item) => {
            return sum + (item?.quantity || 0)
        }, 0) || 0) + totalSelectedBonuses // We'll respect bonus items that we are in the basket.
    const childProductRefs = useRef({})
    let maxOrderQuantity =
        maxBonusItems - selectedItems === 0 ? maxBonusItems : maxBonusItems - selectedItems
    if (maxOrderQuantity < 0) {
        maxOrderQuantity = 0
    }
    return (
        <Stack
            gap={{base: '30px', md: '40px', lg: '30px'}}
            data-promoid={promoId}
            sx={{
                '.product-name': {
                    display: 'block',
                    marginRight: '20px',
                    fontFamily: 'heading',
                    fontWeight: '400',
                    fontSize: {base: '15px', xs: '18px'},
                    letterSpacing: {base: '0.075px', xs: '0.09px'}
                },
                '.product-description': {
                    marginRight: '20px'
                },
                '.product-stack': {
                    gap: {base: '15px', xs: '20px'}
                },
                '.product-view-stack': {
                    gap: {base: '3px', xs: '6px', md: '10px', lg: '6px'},
                    marginLeft: {base: '10px', xs: '20px'},
                    marginRight: 0
                },
                '.product-view-stack b, .product-view-stack s': {
                    fontFamily: 'body',
                    fontWeight: '300',
                    fontSize: {base: '13px', xs: '16px'},
                    letterSpacing: {base: '0.065px', xs: '0.08px'},
                    lineHeight: '22px'
                },
                '.product-view-stack b': {
                    color: 'black.100 !important'
                },
                '.variations-stack': {
                    marginLeft: {base: '10px', xs: '20px'}
                },
                '.product-view__actions': {
                    marginLeft: {base: '10px', xs: '20px'}
                }
            }}
        >
            {bonuses.map((bonus, index) => (
                <Item
                    key={bonus?.productId || bonus?.id || index}
                    bonus={bonus}
                    selectedBonuses={selectedBonuses}
                    childProductRefs={childProductRefs}
                    meterType={meterType}
                    globalePrices={globalePrices}
                    promoId={promoId}
                    selectedItems={selectedItems}
                    index={index}
                    maxBonusItems={maxBonusItems}
                    totalSelectedBonuses={totalSelectedBonuses}
                    bonuses={bonuses}
                    handleClose={handleClose}
                    maxOrderQuantity={maxOrderQuantity}
                    showProductDescription={showProductDescription}
                    setSelectedBonuses={setSelectedBonuses}
                    setShowAddToBagError={setShowAddToBagError}
                />
            ))}
        </Stack>
    )
}

Body.propTypes = {
    bonuses: PropTypes.array,
    maxBonusItems: PropTypes.number,
    globalePrices: PropTypes.any,
    promoId: PropTypes.any,
    bonusItemsInBasketArray: PropTypes.any,
    totalSelectedBonuses: PropTypes.number,
    setSelectedBonuses: PropTypes.func,
    meterType: PropTypes.any,
    setShowAddToBagError: PropTypes.func,
    handleClose: PropTypes.func,
    selectedBonuses: PropTypes.array,
    showProductDescription: PropTypes.bool
}

export default Body
